import { format, isValid } from "date-fns";
import {
  combineDateAndTime,
  hoursMinutesFormat,
  shortDateFormat,
} from "../../../common/helpers/formatDateTime";
import {
  ExpertOutOfOffice,
  ExpertOutOfOfficePeriod,
} from "../../user/types/entities";

export type StatusMessageFormValues = {
  message: string;
  fromDate: Date | null;
  fromTime: string;
  untilDate: Date | null;
  untilTime: string;
};

export const hasStatusMessage = (
  outOfOffice?: ExpertOutOfOffice | null,
): outOfOffice is ExpertOutOfOffice & { from: number; until: number } =>
  Boolean(outOfOffice?.from && outOfOffice?.until);

export const outOfOfficeVersion = (
  outOfOffice?: ExpertOutOfOffice | null,
): number => outOfOffice?.version ?? 0;

export const hasScheduledStatusMessage = (
  outOfOffice?: ExpertOutOfOffice | null,
  now = new Date(),
): outOfOffice is ExpertOutOfOffice & { from: number; until: number } => {
  if (!hasStatusMessage(outOfOffice)) return false;

  const until = new Date(outOfOffice.until);
  return isValid(until) && until.getTime() >= now.getTime();
};

export const isOutOfOfficeActive = (
  outOfOffice?: ExpertOutOfOffice | null,
  now = new Date(),
): boolean => {
  if (!hasScheduledStatusMessage(outOfOffice, now)) return false;

  const from = new Date(outOfOffice.from);
  if (!isValid(from)) return false;

  const time = now.getTime();
  return from.getTime() <= time && time < new Date(outOfOffice.until).getTime();
};

export const formatOutOfOfficePeriod = (
  outOfOffice?: ExpertOutOfOffice | null,
  now = new Date(),
): string | null => {
  if (!hasScheduledStatusMessage(outOfOffice, now)) return null;

  const from = new Date(outOfOffice.from);
  if (!isValid(from)) return null;

  return `${format(from, shortDateFormat)} – ${format(new Date(outOfOffice.until), shortDateFormat)}`;
};

export const defaultStatusMessageFormValues = (
  now = new Date(),
): StatusMessageFormValues => ({
  message: "",
  fromDate: now,
  fromTime: format(now, hoursMinutesFormat),
  untilDate: null,
  untilTime: "",
});

export const mapOutOfOfficeToFormValues = (
  outOfOffice?: ExpertOutOfOffice | null,
): StatusMessageFormValues => {
  if (!hasStatusMessage(outOfOffice)) {
    return defaultStatusMessageFormValues();
  }

  const from = new Date(outOfOffice.from);
  const until = new Date(outOfOffice.until);

  return {
    message: outOfOffice.message ?? "",
    fromDate: from,
    fromTime: format(from, hoursMinutesFormat),
    untilDate: until,
    untilTime: format(until, hoursMinutesFormat),
  };
};

export const mapFormValuesToOutOfOffice = (
  values: StatusMessageFormValues,
): ExpertOutOfOfficePeriod => {
  const from = combineDateAndTime(values.fromDate, values.fromTime);
  const until = combineDateAndTime(values.untilDate, values.untilTime);

  return {
    message: values.message.trim(),
    from: from?.getTime() ?? 0,
    until: until?.getTime() ?? 0,
  };
};
